import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Occupation } from '../types';
import { occupationService } from '../services/occupation/occupationService';

// Re-exported so existing imports (`import { Occupation } from '.../OccupationContext'`)
// keep working while the canonical definition lives in src/types.
export type { Occupation };

interface OccupationContextType {
  occupations: Occupation[];
  selectedOccupation: Occupation | null;
  setSelectedOccupation: (occ: Occupation | null) => void;
  loading: boolean;
}

const OccupationContext = createContext<OccupationContextType | undefined>(undefined);

export function OccupationProvider({ children }: { children: ReactNode }) {
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [selectedOccupation, setSelectedOccupation] = useState<Occupation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    occupationService.getOccupations().then((data) => {
      if (active) {
        setOccupations(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <OccupationContext.Provider
      value={{ occupations, selectedOccupation, setSelectedOccupation, loading }}
    >
      {children}
    </OccupationContext.Provider>
  );
}

/** Low-level context accessor. Prefer the `useOccupation` hook in feature code. */
export function useOccupation() {
  const context = useContext(OccupationContext);
  if (context === undefined) {
    throw new Error('useOccupation must be used within an OccupationProvider');
  }
  return context;
}
